import { useMutation } from '@apollo/client';
import { ASSIGN_EXCEPTION, RETRY_EXCEPTION, UPDATE_EXCEPTION_STATUS_TO_IN_PROGRESS, UPDATE_EXCEPTION_STATUS_TO_OPEN } from '../../graphql/exceptions.graphql';
import { Exception, User } from '../../graphql/graphql';

export function useExceptionHandlers(viewingException: Exception | null | undefined, currentUser: User | undefined, refetch?: () => void) {
  const [assignException,  { loading: assignLoading, error: assignError }] = useMutation(ASSIGN_EXCEPTION);
  const [retryException, { loading: retryLoading, error: retryError }] = useMutation(RETRY_EXCEPTION);
  const [updateExceptionStatusToInProgress, { loading: inProgressLoading, error: inProgressError }] = useMutation(UPDATE_EXCEPTION_STATUS_TO_IN_PROGRESS);
  const [updateExceptionStatusToOpen, { loading: openLoading, error: openError }] = useMutation(UPDATE_EXCEPTION_STATUS_TO_OPEN);

  const assignToMeHandler = async () => {
    if (viewingException?.id && currentUser?.id) {
      await assignException({
        variables: { exceptionId: viewingException.id, assigneeId: currentUser.id },
        optimisticResponse: {
          assignException: {
            __typename: "MutationExceptionPayload",
            exception: {
              ...viewingException,
              assignee: {
                __typename: "User",
                id: currentUser.id,
                name: currentUser.name,
                email: currentUser.email,
              },
            },
          },
        },
        update: (cache, { data }) => {
          const updated = data?.assignException?.exception;
          if (!updated) return;
          cache.modify({
            id: cache.identify({ __typename: "Exception", id: updated.id }),
            fields: { assignee() { return updated.assignee; } },
          });
        },
      });
      if (refetch) setTimeout(() => refetch(), 250);
    }
  };

  const assignToUserHandler = async (user: User) => {
    if (viewingException?.id && user?.id) {
      await assignException({
        variables: { exceptionId: viewingException.id, assigneeId: user.id },
        optimisticResponse: {
          assignException: {
            __typename: "MutationExceptionPayload",
            exception: {
              ...viewingException,
              assignee: {
                __typename: "User",
                id: user.id,
                name: user.name,
                email: user.email,
              },
            },
          },
        },
        update: (cache, { data }) => {
          const updated = data?.assignException?.exception;
          if (!updated) return;
          cache.modify({
            id: cache.identify({ __typename: "Exception", id: updated.id }),
            fields: { assignee() { return updated.assignee; } },
          });
        },
      });
      if (refetch) setTimeout(() => refetch(), 250);
    }
  };

  const retryHandler = async () => {
    if (viewingException?.id) {
      await retryException({
        variables: { exceptionId: viewingException.id },
        update: (cache, { data }) => {
          const updated = data?.retryException?.exception;
          if (!updated) return;
          cache.modify({
            id: cache.identify({ __typename: "Exception", id: updated.id }),
            fields: { status() { return updated.status; } },
          });
        },
      });
      if (refetch) setTimeout(() => refetch(), 250);
    }
  };

  const markAsOpenHandler = async () => {
    if (viewingException?.id) {
      await updateExceptionStatusToOpen({
        variables: { exceptionId: viewingException.id },
        update: (cache, { data }) => {
          const updated = data?.updateExceptionStatusToOpen?.exception;
          if (!updated) return;
          cache.modify({
            id: cache.identify({ __typename: "Exception", id: updated.id }),
            fields: { status() { return updated.status; } },
          });
        },
      });
      if (refetch) setTimeout(() => refetch(), 250);
    }
  };

  const markInProgressHandler = async () => {
    if (viewingException?.id) {
      await updateExceptionStatusToInProgress({
        variables: { exceptionId: viewingException.id },
        update: (cache, { data }) => {
          const updated = data?.updateExceptionStatusToInProgress?.exception;
          if (!updated) return;
          cache.modify({
            id: cache.identify({ __typename: "Exception", id: updated.id }),
            fields: { status() { return updated.status; } },
          });
        },
      });
      if (refetch) setTimeout(() => refetch(), 250);
    }
  };

  return {
    assignToMeHandler,
    assignToUserHandler,
    retryHandler,
    markAsOpenHandler,
    markInProgressHandler,
    assignLoading,
    retryLoading,
    inProgressLoading,
    openLoading,
    assignError,
    retryError,
    inProgressError,
    openError
  };
}